import * as React from 'react';
import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { DropdownFieldProps, useDropdownFieldController } from './dropdown-field.controller';
import { Flex } from '@common/components/Flex';
import { getHeadingAlignmentStyle, getHeadingPositionOffset } from '../field-heading.utils';
import { FieldContainer } from '../field-container';
import { Hint } from '@common/components/hint';
import Dropdown, { components } from '@common/containers/fields/Dropdown';
import { useFieldContentStyle } from '../content-style.utils';
import { DatasheetComponentTextStyle } from '@common/dto/datasheet/component-settings/text-style';
import { useDatasheetNavigationContext, useDatasheetResultFieldEditable, useForceDisabled } from '../../context';

import './dropdown-field.styles.scss';

// Custom Option component that displays colors
const ColoredOption = (props: any) => {
  const { data } = props;
  const optionColor = data?.color;
  const fallbackColor = props?.selectProps?.dropdownContentColor;
  const resolvedColor = optionColor ?? fallbackColor;

  return (
    <components.Option {...props}>
      <span style={{ color: resolvedColor || undefined }}>
        {props.children}
      </span>
    </components.Option>
  );
};

// Custom SingleValue component that displays the selected value with its color
const ColoredSingleValue = (props: any) => {
  const { data } = props;
  const optionColor = data?.color;
  const fallbackColor = props?.selectProps?.dropdownContentColor;
  const resolvedColor = optionColor ?? fallbackColor;

  return (
    <components.SingleValue {...props}>
      <span style={{ color: resolvedColor || undefined }}>
        {props.children}
      </span>
    </components.SingleValue>
  );
};

export const DropdownField: React.FC<DropdownFieldProps> = React.memo((props) => {
  const { datasheetElement } = props;

  const { 
    headingPlacedHorizontally,
    headingReference,
    headingSize,
    headingPosition,
    handleFieldValueSave,
    selectedValue,
    normalizeDropdownOptions,
    currentValue,
  } = useDropdownFieldController(props);

  const isEditable = useDatasheetResultFieldEditable(Boolean(datasheetElement.original.editable));
  const forceDisabled = useForceDisabled();
  const showBorder = datasheetElement.original.showborder !== false;

  const { registerField, focusNext, focusPrev } = useDatasheetNavigationContext();
  const fieldContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isEditable) return;
    registerField(datasheetElement.id, {
      focus: () => {
        const input = fieldContainerRef.current?.querySelector<HTMLInputElement>('input');
        input?.focus();
      },
    });
    return () => { registerField(datasheetElement.id, null); };
  }, [datasheetElement.id, registerField, isEditable]);

  const handleContainerKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Tab' || !isEditable) return;
      e.preventDefault();
      if (e.shiftKey) {
        focusPrev(datasheetElement.id);
      } else {
        focusNext(datasheetElement.id);
      }
    },
    [datasheetElement.id, focusNext, focusPrev, isEditable],
  );

  const { contentColor, contentFontFamily, contentFontSize, contentTextStyles } = useFieldContentStyle(datasheetElement.original);

  const contentFontWeight = contentTextStyles.includes(DatasheetComponentTextStyle.bold) ? 'bold' : undefined;
  const contentFontStyle = contentTextStyles.includes(DatasheetComponentTextStyle.italic) ? 'italic' : undefined;
  const contentTextDecoration = contentTextStyles.includes(DatasheetComponentTextStyle.underline) ? 'underline' : undefined;

  const fieldWidth = datasheetElement.original.width;
  const fieldHeight = datasheetElement.original.height;

  const headingAlignmentStyle = getHeadingAlignmentStyle({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    fieldWidth,
  });

  const headingPositionOffset = getHeadingPositionOffset({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    headingSize,
    fieldWidth,
    fieldHeight,
  });

  const fieldIsEmpty = (() => { const v = (datasheetElement.original as any).field?.name ?? (datasheetElement.original as any).field?.value; return v == null || String(v).trim() === ''; })();

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext} 
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  return (
    <DatasheetItem
      style={{
        top: datasheetElement.coordinates.y,
        left: datasheetElement.coordinates.x,
        width: datasheetElement.width,
      }}
    >
      {labelComponent}
      <Flex alignItems="center" style={{ position: 'relative' }}>
        <FieldContainer
          ref={fieldContainerRef}
          fieldId={datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id}
          onKeyDown={handleContainerKeyDown}
          width={fieldWidth}
          height={fieldHeight}
          showBorder={datasheetElement.original.showborder}
          required={datasheetElement.original.required}
          isEmpty={fieldIsEmpty}
          className={`DatasheetsResultDropdownField__field-container ${!isEditable ? 'DatasheetsResultDropdownField__field-container--disabled' : ''}`}
        >
          <Dropdown
            fullWidth fullHeight
            selectedValue={forceDisabled ? null : selectedValue}
            options={normalizeDropdownOptions}
            onValueChanged={isEditable ? handleFieldValueSave : undefined}
            disabled={!isEditable}
            dropdownContentColor={contentColor}
            styles={{
              control: (base: React.CSSProperties) => ({
                ...base,
                fontFamily: contentFontFamily,
                fontSize: contentFontSize,
                color: contentColor,
                fontWeight: contentFontWeight,
                fontStyle: contentFontStyle,
                textDecoration: contentTextDecoration,
                border: showBorder ? base.border : 'none',
                boxShadow: showBorder ? base.boxShadow : 'none',
                ':hover': {
                  ...(base as any)[':hover'],
                  border: showBorder ? (base as any)[':hover']?.border ?? base.border : 'none',
                },
              }),
              singleValue: (base: React.CSSProperties, state: any) => {
                // Use option color if available, otherwise use contentColor
                const optionColor = state.selectProps.value?.color;
                return {
                  ...base,
                  fontFamily: contentFontFamily,
                  fontSize: contentFontSize,
                  color: optionColor || contentColor,
                  fontWeight: contentFontWeight,
                  fontStyle: contentFontStyle,
                  textDecoration: contentTextDecoration,
                };
              },
              input: (base: React.CSSProperties) => ({
                ...base,
                fontFamily: contentFontFamily,
                fontSize: contentFontSize,
                color: contentColor,
                fontWeight: contentFontWeight,
                fontStyle: contentFontStyle,
                textDecoration: contentTextDecoration,
              }),
              option: (base: React.CSSProperties, state: any) => ({
                ...base,
                fontFamily: contentFontFamily,
                fontSize: contentFontSize,
                color: state.data?.color || contentColor,
                fontWeight: contentFontWeight,
                fontStyle: contentFontStyle,
                textDecoration: contentTextDecoration,
              }),
            }}
            components={{
              Option: ColoredOption,
              SingleValue: ColoredSingleValue,
            }}
          />
        </FieldContainer>
        {datasheetElement.original.helptext && (
          <Hint description={datasheetElement.original.helptext} />
        )}
      </Flex>
    </DatasheetItem>
  );
});

